/**
 * The strategy board — every open decision as a piece on a four-by-four board.
 *
 * Files (a–d) are when it is dealt with; ranks (4–1) how much it matters.
 * Pick a piece up and put it down — by dragging, or by clicking the piece and
 * then the square, which is also how a keyboard or a phone plays it — and the
 * position is re-scored at once: pieces past their own decide-by are in
 * check, "Now" over its focus is overloaded, cash past the budget is
 * overspend. The room's reply for the piece in hand is offered, the moves are
 * listed and can be taken back, and the position can be saved.
 */
import { useMemo, useState, type DragEvent } from "react";
import {
  bestSquare,
  BOARD_COLS,
  BOARD_ROWS,
  describeMove,
  evaluateBoard,
  squareName,
  suggestedPlacement,
  type BoardSettings,
  type Move,
  type Piece,
  type Placement,
  type Square,
} from "../../domain/ceoRoom";
import { money } from "../../domain/intelligence";
import { Button, Card, Field, Input } from "../../design/components";
import { t } from "../../i18n";
import type { SavedBoard } from "./roomStore";
import c from "./CeoRoom.module.css";

/** The piece each kind of decision is drawn as. */
export const GLYPH: Record<string, string> = {
  capital: "♛",
  quarter: "♚",
  regulator: "♜",
  runway: "♞",
  peak: "♝",
  matter: "♟",
};

const GLYPH_LEGEND: { glyph: string; label: string }[] = [
  { glyph: "♛", label: "Capital" },
  { glyph: "♚", label: "The quarter" },
  { glyph: "♜", label: "Regulator" },
  { glyph: "♞", label: "Airfield" },
  { glyph: "♝", label: "Readiness" },
  { glyph: "♟", label: "Your own matter" },
];

const COL_HINT = ["within 14 days", "within 90 days", "within a year", "after a year"];

export function StrategyBoard({
  pieces,
  saved,
  currency,
  onSave,
  onOpen,
}: {
  pieces: Piece[];
  saved: SavedBoard | null;
  currency: string;
  onSave: (b: SavedBoard) => Promise<"platform" | "browser">;
  onOpen: (p: Piece) => void;
}) {
  const opening = useMemo(() => suggestedPlacement(pieces), [pieces]);
  /* a saved square for a piece that is still open; the opening square for the rest */
  const [placement, setPlacement] = useState<Placement>(() => ({ ...opening, ...pick(saved?.placement ?? {}, pieces) }));
  const [settings, setSettings] = useState<BoardSettings>(saved?.settings ?? { budget: null, focus: 3 });
  const [held, setHeld] = useState<string | null>(null);
  const [moves, setMoves] = useState<Move[]>([]);
  const [status, setStatus] = useState<string | null>(saved?.at ? t("Position saved {date} by {name}.", { date: saved.at.slice(0, 10), name: saved.by ?? "—" }) : null);

  /* pieces that arrived after the position was saved go to their opening squares */
  const live = useMemo<Placement>(() => ({ ...opening, ...pick(placement, pieces) }), [opening, placement, pieces]);
  const ev = useMemo(() => evaluateBoard(pieces, live, settings), [pieces, live, settings]);
  const heldPiece = pieces.find((p) => p.id === held) ?? null;
  const reply = heldPiece ? bestSquare(pieces, live, settings, heldPiece.id) : null;
  const checked = new Set(ev.checks.filter((k) => k.pieceId).map((k) => k.pieceId as string));

  const move = (id: string, to: Square) => {
    const from = live[id];
    const p = pieces.find((x) => x.id === id);
    if (!from || !p || (from.col === to.col && from.row === to.row)) return;
    const next = { ...live, [id]: to };
    const after = evaluateBoard(pieces, next, settings).score;
    setMoves((ms) => [...ms, { pieceId: id, title: p.title, from, to, before: ev.score, after }]);
    setPlacement(next);
    setHeld(null);
    setStatus(null);
  };
  const undo = () => {
    const last = moves[moves.length - 1];
    if (!last) return;
    setPlacement({ ...live, [last.pieceId]: last.from });
    setMoves((ms) => ms.slice(0, -1));
  };
  const save = async () => {
    const where = await onSave({ placement: live, settings, by: undefined, at: new Date().toISOString() });
    setStatus(where === "platform" ? t("Position saved on the platform, under your name.") : t("Position saved in this browser only — the platform did not take it."));
  };

  const onDrop = (e: DragEvent<HTMLDivElement>, sq: Square) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (id) move(id, sq);
  };

  const tone = ev.score >= 80 ? c.good : ev.score >= 60 ? c.fair : ev.score >= 40 ? c.warnTone : c.badTone;

  return (
    <div className={c.boardLayout}>
      <div className={c.boardCol}>
        <p className={c.howTo}>
          {t("Every open decision is a piece. Across: when you deal with it. Down: how much it matters. Drag a piece — or click it, then click a square — and the score on the right changes at once, with the reason.")}
        </p>
        <div className={c.board} role="grid" aria-label={t("Strategy board")} data-testid="strategy-board">
          <div className={c.corner} aria-hidden />
          {BOARD_COLS.map((col, i) => (
            <div key={col.key} className={c.fileHead} role="columnheader">
              <b>
                {col.file} · {t(col.label)}
              </b>
              <span>{t(COL_HINT[i])}</span>
            </div>
          ))}
          {BOARD_ROWS.map((row, r) => (
            <div key={row.key} className={c.rankRow} role="row">
              <div className={c.rankHead} role="rowheader">
                <b>{row.rank}</b>
                <span>{t(row.label)}</span>
              </div>
              {BOARD_COLS.map((col, ci) => {
                const sq = { col: ci, row: r };
                const here = pieces.filter((p) => live[p.id]?.col === ci && live[p.id]?.row === r);
                const target = heldPiece && reply && reply.square.col === ci && reply.square.row === r;
                return (
                  <div
                    key={col.key}
                    role="gridcell"
                    className={`${c.square} ${(ci + r) % 2 ? c.dark : c.light} ${held ? c.canDrop : ""} ${target ? c.suggest : ""}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, sq)}
                    onClick={() => held && move(held, sq)}
                    aria-label={t("square {name}: {file}, {rank}", { name: squareName(sq), file: t(col.label), rank: t(row.label) })}
                    data-testid={`square-${squareName(sq)}`}
                  >
                    <span className={c.sqName} aria-hidden>
                      {squareName(sq)}
                    </span>
                    {here.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", p.id);
                          setHeld(p.id);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setHeld(held === p.id ? null : p.id);
                        }}
                        className={`${c.piece} ${p.kind === "matter" ? c.pieceMine : ""} ${held === p.id ? c.pieceHeld : ""} ${checked.has(p.id) ? c.pieceCheck : ""}`}
                        aria-pressed={held === p.id}
                        aria-label={t("{title} — on {square}", { title: p.title, square: squareName(sq) })}
                        data-testid={`piece-${p.id}`}
                        title={p.title}
                      >
                        <span className={c.glyph} aria-hidden>
                          {GLYPH[p.frame] ?? "♟"}
                        </span>
                        <span className={c.pieceTitle}>{p.title}</span>
                        {checked.has(p.id) && <span className={c.checkMark}>{t("check")}</span>}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <ul className={c.legend} aria-label={t("The pieces")}>
          {GLYPH_LEGEND.map((g) => (
            <li key={g.glyph}>
              <span className={c.glyph}>{g.glyph}</span> {t(g.label)}
            </li>
          ))}
        </ul>
      </div>

      <div className={c.sideCol}>
        <section className={`${c.evalBox} ${tone}`} aria-label={t("The position")} data-testid="board-eval">
          <div className={c.evalTop}>
            <span className={c.evalN}>{ev.score}</span>
            <span className={c.evalOf}>/100</span>
            <span className={c.evalGrade}>{t(ev.grade)}</span>
          </div>
          <div className={c.evalTrack}>
            <span style={{ width: `${ev.score}%` }} />
          </div>
          {moves.length > 0 && <p className={c.lastMove}>{describeMove(moves[moves.length - 1])}</p>}
          {ev.checks.length === 0 ? (
            <p className={c.muted}>{t("Nothing late, nothing buried, nothing over the focus or the budget.")}</p>
          ) : (
            <ul className={c.checks}>
              {ev.checks.map((k, i) => (
                <li key={i}>
                  <b>−{k.cost}</b> {k.text}
                </li>
              ))}
            </ul>
          )}
        </section>

        {heldPiece && (
          <Card title="In your hand" sub={heldPiece.title}>
            <p className={c.muted}>
              {heldPiece.decideByDays != null ? t("Due in {n} days.", { n: heldPiece.decideByDays }) : t("No decide-by date.")}{" "}
              {heldPiece.cost != null ? t("Commits {amount}.", { amount: money(heldPiece.cost, currency) }) : ""}
            </p>
            {reply && (
              <p>
                {t("The room's move: {square}, for a position of {score}.", { square: squareName(reply.square), score: reply.score })}
              </p>
            )}
            <div className={c.row}>
              {reply && (
                <Button size="sm" variant="primary" onClick={() => move(heldPiece.id, reply.square)} disabled={reply.score <= ev.score}>
                  {t("Play the room's move")}
                </Button>
              )}
              <Button size="sm" onClick={() => onOpen(heldPiece)}>
                {t("Open this decision")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setHeld(null)}>
                {t("Put it down")}
              </Button>
            </div>
          </Card>
        )}

        <Card title="Your limits" sub="What the position is judged against">
          <div className={c.limits}>
            <Field label="Budget for Now and This quarter">
              <Input
                type="number"
                min={0}
                value={settings.budget ?? ""}
                placeholder={t("not set")}
                onChange={(e) => setSettings({ ...settings, budget: e.target.value === "" ? null : Number(e.target.value) })}
                aria-label={t("Budget for Now and This quarter")}
              />
            </Field>
            <Field label="Critical or high at once in Now">
              <Input
                type="number"
                min={1}
                max={12}
                value={settings.focus}
                onChange={(e) => setSettings({ ...settings, focus: Math.max(1, Number(e.target.value) || 1) })}
                aria-label={t("Critical or high at once in Now")}
              />
            </Field>
          </div>
          <table className={c.cashTable}>
            <tbody>
              {BOARD_COLS.map((col, i) => (
                <tr key={col.key}>
                  <td>
                    {col.file} · {t(col.label)}
                  </td>
                  <td className={c.numCell}>{money(ev.cash[i], currency)}</td>
                </tr>
              ))}
              <tr className={c.cashTotal}>
                <td>{t("Committed in Now and This quarter")}</td>
                <td className={c.numCell}>{money(ev.committed, currency)}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        <Card title="Moves" sub={moves.length ? t("{n} this session", { n: moves.length }) : t("none yet")}>
          {moves.length > 0 && (
            <ol className={c.moveList} data-testid="move-list">
              {moves.map((m, i) => (
                <li key={i}>{describeMove(m)}</li>
              ))}
            </ol>
          )}
          <div className={c.row}>
            <Button size="sm" onClick={undo} disabled={!moves.length}>
              {t("Take back")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setPlacement(opening);
                setMoves([]);
                setHeld(null);
              }}
            >
              {t("The room's opening")}
            </Button>
            <Button size="sm" variant="primary" onClick={() => void save()} data-testid="save-board">
              {t("Save this position")}
            </Button>
          </div>
          {status && <p className={c.savedNote}>{status}</p>}
        </Card>
      </div>
    </div>
  );
}

/** Only the squares of pieces still on the board. */
function pick(pl: Placement, pieces: Piece[]): Placement {
  const out: Placement = {};
  for (const p of pieces) if (pl[p.id]) out[p.id] = pl[p.id];
  return out;
}
